import { pushHistory } from '../state/history.js';
import { requestRender } from '../state/bus.js';
import { syncPlan } from './plan-view.js';
import { $, S, clamp, el, fmt } from '../state/store.js';
import { buildHandles, cornerCheck, cornerRefine, manualSetup, markStale, paintHandles, placementOnly, showCalibView } from '../ui/calibrate-view.js';
import { captureFocus, deferRail, isEditing, numRow, pairRow, panel, readout, restoreFocus, scrubbing, selectRow } from '../ui/controls.js';
import { metersFromDiagonalInches, onResChange, onScreenChange, pxAspect, renderArrayInspector, syncCornerWidth } from './rail-right.js';

/* Calibrate's settings sidebar — Screen/Corners/Lens/Result. This used to be
   a generic docked rail shared by every stage; Design and Export moved
   everything into the right dock (see ui/rail-right.js) once nothing in
   either stage needed a left rail any more, and Calibrate itself has since
   become its own focused view (index.html's #calibPanel, beside the photo)
   rather than a rail-based stage — this file just renders into that panel
   now. Locking the angle and advancing to Design both happen on the photo
   itself (#lockAngle/#goCompose, see ui/calibrate-view.js) — this only
   surfaces the numeric result once solved. */

export function renderLeft(){
  if(scrubbing || isEditing()){ deferRail('L'); return; }
  const R = $('#calibPanel');
  const focus = captureFocus(R);
  // Same scroll-anchoring as renderRight — see the note there.
  const scroll = R.scrollTop;
  /* The thumbnail's <svg> is created empty by the body above, so it is filled
     after the rebuild rather than during it — and in a finally, because a rail
     that threw half-way should still not leave an empty box where the shape
     goes. */
  try{ renderLeftBody(R); } finally { restoreFocus(R, focus); R.scrollTop = scroll; syncPlan(); }
}
/* Typing into a field that chains a rebuild into its own onChange (Pixels
   across/down, which also resize the screen) used to lose focus the moment
   the DOM was torn down and rebuilt — see controls.js's captureFocus for
   why. Split out so the wrapper above can restore it however this body
   returns. */
function renderLeftBody(R){
  R.innerHTML='';

  if(S.stage==='calib'){
    /* ── screen ──
       An array's screens are specified in its own panel (LCD Video Wall,
       ui/rail-right.js), not measured here — placementOnly() (calibrate-view.js)
       means these marks position an already-built wall in a photo and never
       touch S.screen/S.res/S.cornerPanel. Pixel counts, Swap, a second display,
       the resolution/display-size presets: every one of them edits a single
       screen's shape, which an array does not have. */
    /* A wall sets its size in its own panel — the same one Design has — so it
       can be got right BEFORE the corners are marked: the solve measures the
       viewer against exactly that size. */
    if(S.screenArray) renderArrayInspector(R);
    if(!S.screenArray){
    const p1 = panel('Screen', 'real world');

    /* THE SHAPE, THEN THE NUMBERS THAT DESCRIBE IT.

       The four measurement rows are contiguous and nothing is allowed between
       them. They were split by the flip button and the resolution preset, which
       put two rows of one rectangle either side of two controls — and reading
       a rectangle down a column is the whole reason they were paired up. The
       presets moved below, where they are what they are: shortcuts that write
       into the rows above. */
    const thumb = el('button','planthumb');
    thumb.type = 'button';
    thumb.title = 'Open the plan beside the photo';
    const mini = document.createElementNS('http://www.w3.org/2000/svg','svg');
    mini.setAttribute('viewBox','0 0 260 72');
    mini.id = 'planMini';
    thumb.appendChild(mini);
    thumb.appendChild(el('span', null, S.cornerPanel
      ? `${S.res.w} + ${S.cornerPanel.resW} × ${S.res.h} px · corner`
      : `${S.res.w} × ${S.res.h} px · ${fmt(S.screen.w,2)} × ${fmt(S.screen.h,2)} m`));
    thumb.onclick = ()=>showCalibView('plan');
    p1.body.appendChild(thumb);

    /* Pixels are the source of truth for shape: they're what the content is
       actually authored at. Metres describe how big that grid is built in the
       real world, so their ratio always matches the pixel ratio — editing one
       recomputes the other rather than letting the two drift apart into a
       rectangle that doesn't match the artwork. */
    const onPx = () => { S.screen.h = S.screen.w/pxAspect(); onResChange(); onScreenChange(); renderLeft(); };
    p1.body.appendChild(pairRow('Grid', [
      { value:S.res.w, step:1, key:'Pixels across',
        onChange:v=>{ S.res.w = Math.max(1,Math.round(v)); onPx(); } },
      { value:S.res.h, step:1, key:'Pixels down',
        onChange:v=>{ S.res.h = Math.max(1,Math.round(v)); onPx(); } }
    ], 'px'));
    /* The second panel's row sits DIRECTLY under the first's, in the same
       columns, so the two pixel counts are a pair you can compare at a glance.
       It carries both its numbers even though only the width is yours: a panel
       whose height you cannot read is a panel you have to work out, and not
       having to is the whole point of the shared edge. */
    if(S.cornerPanel)
      p1.body.appendChild(pairRow('Second', [
        { value:S.cornerPanel.resW, step:1, key:'Right panel pixels',
          onChange:v=>{
            S.cornerPanel.resW = Math.max(1, Math.round(v));
            syncCornerWidth(); markStale(); onScreenChange(); renderLeft();
          } },
        { text:String(S.res.h), title:'The shared edge — the same height as the first panel' }
      ], 'px', { mark:true, title:'The second display, across the corner' }));
    p1.body.appendChild(pairRow('Size', [
      { value:S.screen.w, step:.01, key:'Width',
        onChange:v=>{ S.screen.w=Math.max(.02,v); S.screen.h = S.screen.w/pxAspect(); onScreenChange(); } },
      { value:S.screen.h, step:.01, key:'Height',
        onChange:v=>{ S.screen.h=Math.max(.02,v); S.screen.w = S.screen.h*pxAspect(); onScreenChange(); } }
    ], 'm'));
    /* Both derived: the width from the pixel count at the first panel's pitch,
       the height because it IS the first panel's height. It was typeable for a
       while, to express a corner whose two panels carry different pixel
       pitches — which is a real installation, just not the one that prompted
       it. The seam it was meant to fix turned out to be the warp, not the
       width, so the control was answering a question nobody had asked and is
       gone again. One number in, both sides out. */
    if(S.cornerPanel)
      p1.body.appendChild(pairRow('Second', [
        { text:fmt(S.cornerPanel.w,2), title:'That pixel count at the first panel’s pitch' },
        { text:fmt(S.screen.h,2),      title:'The shared edge — the same height as the first panel' }
      ], 'm', { mark:true }));
    /* A bracket under the pair, saying in one line what the two rows have in
       common — that they are one rectangle in two units, and that editing
       either redraws the other. Cheaper than discovering it by typing. */
    const asp = el('div','aspect');
    asp.appendChild(el('span'));
    asp.appendChild(el('span', null, S.cornerPanel
      ? 'Same aspect, and the same pitch across the corner'
      : 'Same aspect — editing one side redraws the other'));
    p1.body.appendChild(asp);

    /* Two buttons on one line, the way the pair above is one line: turning the
       screen, and adding the second one. Both change the SHAPE, which is what
       this group of rows is. */
    const acts = el('div','btnrow');
    /* "Swap", not "Turn upright". This is a two-way toggle and that label
       described one direction of it: on a screen that is already portrait,
       "Turn upright" made it landscape — the opposite of what it promised.
       Its own tooltip had always said "swap portrait and landscape", so the
       label was the only part that was wrong.

       Not "Rotate" either. Nothing is rotated: the width and height trade
       places, in pixels and in metres. "Rotate" in a design tool reads as
       something happening to the artwork. */
    const flipBtn = el('button','btn btn--sm btn--gh','⇅ Swap');
    flipBtn.title = 'Swap width and height — portrait becomes landscape and back, pixels and physical size together';
    flipBtn.onclick = ()=>{
      [S.res.w, S.res.h] = [S.res.h, S.res.w];
      [S.screen.w, S.screen.h] = [S.screen.h, S.screen.w];
      onResChange(); onScreenChange(); renderLeft();
      pushHistory();
    };
    acts.appendChild(flipBtn);
    /* Still gated on the photo: switching it on seeds the two extra marks by
       mirroring the four you have placed, which needs marks to mirror. */
    if(S.photo){
      const cb = el('button','btn btn--sm '+(S.cornerPanel?'btn--pri':'btn--gh'),
        S.cornerPanel ? '✓ Second display' : '＋ Second display');
      cb.title = 'A screen that wraps a corner as two panels at 90°';
      cb.setAttribute('aria-pressed', S.cornerPanel ? 'true' : 'false');
      cb.onclick = ()=>{
        if(S.cornerPanel){ S.cornerPanel = null; }
        else{
          /* Seed the two new marks as a rough mirror of the left panel about the
             shared edge, so they land somewhere plausible on the photo rather
             than stacked at the origin waiting to be found. The pixels start as
             a copy of the first panel — a fair guess for a fabricated totem, and
             the width the solve can measure back for you. */
          const m = (a,b) => ({ x: clamp(2*b.x - a.x, 0, 1), y: clamp(2*b.y - a.y, 0, 1) });
          S.cornerPanel = {
            w: S.screen.w, resW: S.res.w, convex: true,
            outer: { tr: m(S.corners.tl, S.corners.tr), br: m(S.corners.bl, S.corners.br) }
          };
        }
        buildHandles();
        paintHandles(false);   // laying out handles is not the user moving them
        /* A recess auto-fits to the screen, and a corner screen's recess is a
           different SHAPE — a notch across the corner rather than a box behind
           one plane. onScreenChange is what already refits every recess and
           frame; the rescale inside it is a no-op when the screen itself has
           not moved. */
        onScreenChange();
        markStale();
        renderLeft();
      };
      acts.appendChild(cb);
    }
    p1.body.appendChild(acts);
    if(S.photo)
      p1.body.appendChild(el('p','note', S.cornerPanel
        ? 'Only the second display’s pixels across are yours to set. Its height is the edge both panels share, and its width in metres is that pixel count at the same pitch — both shown above, neither typed.'
        : 'For a screen that wraps a corner as two panels. You mark the corner edge once and it belongs to both.'));

    /* Shortcuts, below the thing they write into. They are not measurements of
       this screen, they are ways of filling those measurements in. */
    p1.body.appendChild(el('div','rule'));
    p1.body.appendChild(selectRow('Resolution','', [
      ['','Common resolutions…'],
      ['1080x1920','1080 × 1920 — vertical'],['1920x1080','1920 × 1080 — 16:9'],
      ['3840x2160','3840 × 2160 — 4K 16:9'],['1080x1080','1080 × 1080 — square'],
      ['2160x3840','2160 × 3840 — vertical 4K']
    ], v=>{
      if(!v) return;
      const [w,h] = v.split('x').map(Number);
      S.res.w=w; S.res.h=h; S.screen.h = S.screen.w/pxAspect();
      onResChange(); onScreenChange(); renderLeft();
    }));
    p1.body.appendChild(selectRow('Display size','', [
      ['','Calculate from inches…'],['32','32\"'],['42','42\"'],['55','55\"'],['65','65\"'],['75','75\"']
    ], v=>{
      if(!v) return;
      const {w,h} = metersFromDiagonalInches(parseFloat(v), S.res.w, S.res.h);
      S.screen.w=w; S.screen.h=h; onScreenChange(); renderLeft();
    }));
    const par = (S.res.w/S.res.h)/(S.screen.w/S.screen.h);
    if(Math.abs(par-1)>0.02)
      p1.body.appendChild(el('p','note note--warn',`Pixels aren’t square — ${fmt(par,3)}:1. The render accounts for it; the output will look stretched anywhere else.`));

    R.appendChild(p1);
    }

    /* ── corners ── */
    const p2 = panel('Corners', S.photo ? 'marked' : 'no photo');
    if(!S.photo){
      p2.body.appendChild(el('p','note','Load a reference photo to start marking. The four crosshairs go on the corners of the screen exactly as it appears in the shot.'));
    }else{
      /* The pixel coordinates used to be listed here. You place the crosshairs by
         eye against the photo, so the numbers were never something to read or act
         on — just clutter beside the thing you are actually looking at. */
      /* An LCD video wall's marks are placement, not calibration — see
         ui/calibrate-view.js's placementOnly(). Said here as well as in the
         footer because this is the panel someone reads while dragging, and the
         one thing they need to know is that it cannot break what they built. */
      p2.body.appendChild(el('p','note', S.screenArray
        ? 'Drag the four crosshairs onto the corners of the WHOLE wall — the outside of the outermost screens, not one panel. Arrow keys nudge, Shift+arrow moves further.'
        : 'Drag each crosshair onto its corner. Arrow keys nudge, Shift+arrow moves further.'));
      if(S.screenArray)
        p2.body.appendChild(el('p','note', placementOnly()
          ? 'This is a placement, not a calibration. The viewer was set by hand, so nothing here changes the wall, the viewer, or any file it exports — the marks only say where the wall sits in this photograph.'
          : 'The wall’s size comes from the panel above, so set that first: the marks then tell the app where the audience stands, and Lock angle places the viewer there. Free layouts are marked by their overall outline.'));
      const b = el('button','btn btn--sm btn--gh btn--w','Reset to a centred rectangle');
      b.onclick = ()=>{ S.corners={tl:{x:.22,y:.28},tr:{x:.78,y:.24},br:{x:.80,y:.70},bl:{x:.20,y:.74}}; paintHandles(); renderLeft(); };
      p2.body.appendChild(b);

      if(S.cornerPanel){
        p2.body.appendChild(el('p','note','Six crosshairs now. The two red ones are the corner itself — they are the right edge of the left panel and the left edge of the right one at the same time, so moving either moves both panels.'));
        const pitch = S.res.w / Math.max(1e-6, S.screen.w);
        /* What the two outer marks actually say, now that the camera is known
           from the first panel's four. They took no part in that solve, so this
           is a genuine independent test of the two things a corner screen
           assumes: that the corner is square, and that the second panel is as
           wide as its pixel count says. Shown here rather than only in the toast at solve time —
           it is the number that explains a preview which looks subtly wrong,
           and a toast that flashed past ten minutes ago cannot do that. */
        const chk = cornerCheck();
        if(chk && !chk.error){
          p2.body.appendChild(el('div','rule'));
          const off = Math.abs(chk.wMeasured - S.cornerPanel.w);
          const rows = [
            ['Measured width', fmt(chk.wMeasured,2)+' m', off > .05 ? 'hi' : ''],
            ['Marks are out by', Math.round(chk.reprojPx)+' px  ·  '+fmt(chk.reprojM,2)+' m'],
            ['Corner', chk.convex ? 'outside (convex)' : 'inside (concave)']
          ];
          /* The numbers above are the INDEPENDENT check — panel one's four
             marks placed the camera and the outer two had no say, which is
             what makes them a test rather than a self-assessment. The camera
             actually used is then refined over all six, so this says what that
             bought: without it the whole of the disagreement lands on the
             second panel and tears the On-site seam. */
          if(cornerRefine)
            rows.push(['Camera fitted to', 'all six marks  ·  '
              + Math.round(cornerRefine.rmsBefore) + ' → ' + Math.round(cornerRefine.rmsAfter) + ' px']);
          p2.body.appendChild(readout(rows));
          /* Offered as the pixel count it comes to, not as the metres, because
             pixels is the only number this panel takes. Rounding it to whole
             pixels is why the button says what it will actually set rather
             than what was measured. */
          const wantPx = Math.max(1, Math.round(chk.wMeasured * pitch));
          if(off > .02 && chk.widthTrustworthy && wantPx !== S.cornerPanel.resW){
            const ub = el('button','btn btn--sm btn--w btn--pri',
              `Use the measured width — ${wantPx} px`);
            ub.onclick = ()=>{
              S.cornerPanel.resW = wantPx;
              syncCornerWidth(); markStale(); onScreenChange(); renderLeft();
            };
            p2.body.appendChild(ub);
          }
          if(!chk.widthTrustworthy)
            p2.body.appendChild(el('p','note','The second panel is seen almost edge-on from this viewpoint, so its measured width is unreliable — trust the value you entered.'));
          else if(chk.reprojPx > 8)
            p2.body.appendChild(el('p','note note--warn',
              `The marks do not match a square corner with that width. On-site will look subtly stretched on the second panel until they agree — adjust the second panel's pixel count in Screen, or re-mark the two outer crosshairs.`));
        }
      }
    }
    R.appendChild(p2);

    /* ── lens ──
       Solves for the viewer's own focal length — meaningless on the placement
       path, which never solves at all (see the note on p1 above). */
    if(!S.screenArray || !placementOnly()){
    const p3 = panel('Lens', S.focal.mode);
    p3.body.appendChild(selectRow('Focal length', S.focal.mode, [
      ['auto','Recover from the quad'],['manual','I know what I shot']
    ], v=>{S.focal.mode=v; renderLeft();}));
    if(S.focal.mode==='manual')
      p3.body.appendChild(numRow('35 mm equiv. (iPhone default)', S.focal.manualMM, .5, v=>{S.focal.manualMM=v;}, 'mm'));
    else
      p3.body.appendChild(el('p','note','Recovering the lens from four points is sensitive. If the numbers look off, or the photo was shot dead-on, switch to manual.'));
    /* The way out when marking cannot be made to work — beside what it
       replaces, because that is what it is about, and quiet, because it was
       previously a full-weight button in the footer sitting next to Lock
       angle. Two equal-looking buttons for "finish" and "give up" is not a
       choice anyone should have to read twice. */
    const mb = el('button','btn btn--sm btn--quiet btn--w','Set the viewer by hand instead');
    mb.onclick = manualSetup;
    p3.body.appendChild(mb);
    R.appendChild(p3);
    }
    // Locking the angle and advancing to Design happen together on the
    // photo itself (#lockAngle/#goCompose, see ui/calibrate-view.js) — by
    // the time a solve succeeds S.stage is already 'compose', so the
    // result readout belongs in Master Settings' Calibration panel
    // (ui/rail-right.js), not here.
  }

  // Design and Export don’t render anything into this panel — it only ever
  // shows Calibrate’s Screen/Corners/Lens/Result content (see #calibPanel
  // in index.html, which stays hidden outside Calibrate).
}
